import _ from "lodash";
import { LOADING_NETWORK_STATUSES } from "./constants";

/**
 * Checks if GraphQL query is in loading state
 * @param {Object} data - Query data
 * @param {number} networkStatus - Apollo NetworkStatus
 * @returns {boolean}
 */
export const isQueryLoading = (data, networkStatus) => {
  return _.isEmpty(data) && _.includes(LOADING_NETWORK_STATUSES, networkStatus);
};

/**
 * Transforms template edges to dropdown options
 * @param {Array} templates - Array of template objects or edges
 * @param {boolean} hasEdgesStructure - Whether templates have edges structure
 * @param {Function} t - i18n translation function
 * @returns {Array} Array of {value, label} options
 */
export const getTemplateOptions = ({
  templates,
  hasEdgesStructure = true,
  t,
}) => {
  if (hasEdgesStructure) {
    return _.map(templates, ({ node }) => ({
      value: node.id,
      label: node.name || t("common:untitled"),
    }));
  }

  return _.map(templates, ({ id, name }) => ({
    value: id,
    label: name || t("common:untitled"),
  }));
};

/**
 * Formats multiple school labels into readable string
 * Example: ["School A", "School B", "School C"] -> "School A, School B & School C"
 * @param {Array} schoolIds - Array of school IDs
 * @param {Object} schoolIdToSchoolDataMap - Map of school ID to school data
 * @returns {string}
 */
export const formatSchoolLabels = (schoolIds, schoolIdToSchoolDataMap) => {
  const labels = _.chain(schoolIds)
    .map(id => _.get(schoolIdToSchoolDataMap, [id, "label"], ""))
    .compact()
    .value();

  if (_.size(labels) === 0) return "";
  if (_.size(labels) === 1) return _.first(labels);

  const lastLabel = labels.pop();
  return _.join([_.join(labels, ", "), lastLabel], " & ");
};

/**
 * Groups students by school for multi-school scenario
 * @param {Array} students - Array of student objects
 * @param {Object} studentIdToSchoolIdMap - Map of student ID to school ID
 * @param {Object} schoolIdToSchoolDataMap - Map of school ID to school data
 * @returns {Array} Array of school groups with students
 */
export const groupStudentsBySchool = (
  students,
  studentIdToSchoolIdMap,
  schoolIdToSchoolDataMap
) => {
  const schoolWiseStudents = _.groupBy(students, student =>
    _.get(studentIdToSchoolIdMap, student.id)
  );

  return _.map(schoolWiseStudents, (students, schoolId) => {
    const { label, logo } = _.get(schoolIdToSchoolDataMap, schoolId, {});
    return {
      id: schoolId,
      name: label,
      src: logo,
      options: students,
      shape: "square",
    };
  });
};
